import React from "react";
import { motion } from "framer-motion";
import {
  Stethoscope,
  Utensils,
  Flower2,
  Leaf,
  Heart,
  Activity,
  MessageCircle,
  Calendar,
  BrainCircuit,
} from "lucide-react";

const services = [
  {
    title: "Clinical Nutrition Counseling",
    description: "One-on-one consultations.",
    benefits: "Manage diabetes, PCOS, hypertension, digestive issues.",
    icon: Stethoscope,
  },
  {
    title: "Personalized Diet Plans",
    description: "Tailored meal plans.",
    benefits: "Sustainable habits for weight management and energy.",
    icon: Utensils,
  },
  {
    title: "Yoga–Nutrition Integration Workshops",
    description: "Interactive workshops.",
    benefits: "Supports digestion, reduces stress, mind-body connection.",
    icon: Flower2,
  },
  {
    title: "Sustainable Eating Guidance",
    description: "Eco-friendly food choices.",
    benefits: "Seasonal, local, and minimally processed foods.",
    icon: Leaf,
  },
  {
    title: "Holistic Wellness Packages",
    description: "Nutrition coaching + yoga.",
    benefits: "Physical vitality and emotional balance.",
    icon: Heart,
  },
  {
    title: "Continuous Dietary Monitoring",
    description: "Regular check-ins.",
    benefits: "Ongoing accountability and motivation.",
    icon: Activity,
  },
  {
    title: "Immediate Query Resolution",
    description:
      "Support via phone, messaging, or email whenever you need help or have questions.",
    benefits:
      "Confidence and clarity as you implement your plan-no more confusion or delays.",
    icon: MessageCircle,
  },
  {
    title: "Menu Planning for Wellness Retreats",
    description:
      "Custom-designed menus for wellness retreats that nourish and delight participants.",
    benefits:
      "Balanced, themed meals that align perfectly with your retreat's focus and guests' health goals.",
    icon: Calendar,
  },
  {
    title: "Behaviour Change Support",
    description:
      "Practical strategies and coaching to help you build and sustain healthy habits.",
    benefits:
      "Overcome obstacles, stay motivated, and make meaningful, lasting changes to your lifestyle.",
    icon: BrainCircuit,
  },
];

const Services = () => {
  return (
    <section
      id="services"
      className="py-32 bg-background relative overflow-hidden"
    >
      {/* Subtle background element */}
      <div className="absolute top-0 right-0 w-1/3 h-full bg-linear-to-l from-white/40 to-transparent pointer-events-none" />

      <div className="container mx-auto px-6 md:px-12">
        <div className="max-w-3xl mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs font-bold tracking-[0.2em] text-muted-foreground uppercase mb-4 block">
              Our Expertise
            </span>
            <h2 className="font-serif text-4xl md:text-5xl font-semibold text-foreground mb-6 leading-tight">
              Compassionate,{" "}
              <span className="italic text-muted-foreground">
                evidence-based
              </span>{" "}
              care.
            </h2>
            <p className="text-lg text-muted-foreground font-light max-w-xl leading-relaxed">
              We believe in a holistic approach that integrates clinical
              nutrition with mindful living practices.
            </p>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05, duration: 0.6 }}
              className="group relative p-8 rounded-3xl bg-white/30 backdrop-blur-md border border-white/20 hover:bg-white/50 transition-all duration-500 hover:shadow-xl hover:shadow-stone-200/20 hover:border-white/40"
            >
              <div className="w-14 h-14 bg-white/40 text-secondary-foreground rounded-2xl flex items-center justify-center mb-8 group-hover:scale-110 transition-transform duration-500 shadow-inner border border-white/20">
                <service.icon size={26} strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-serif font-semibold text-foreground mb-3 group-hover:translate-x-1 transition-transform duration-300">
                {service.title}
              </h3>
              <p className="text-muted-foreground font-light mb-4 leading-relaxed">
                {service.description}
              </p>
              <div className="pt-4 border-t border-black/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">Benefits:</span>{" "}
                  {service.benefits}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
